import React, { useState, useEffect } from "react";
import { HelmetProvider } from 'react-helmet-async';
import Header from "./components/Header";
import Footer from "./components/Footer";
import Contactlogo from "./components/Contactlogo";
import GuestRoute from './components/GuestRoute';
import ScrollToTop from './components/ScrollToTop';
import PageTransition from './components/PageTransition';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import Body from "./components/Body";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Search from "./pages/Search";
import Loader from "./components/Loader";
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import Maintenance from "./pages/Maintenance";
import TermsConditions from './pages/TermsConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GetVideos from "./pages/video/GetVideo";
import AddVideo from "./pages/video/AddVideo";
import UpdateVideo from "./pages/video/UpdateVideo";
import Branches from './pages/Branches';
import Units from './pages/units/Units';
import SignUp from './pages/SignUp';
import LoginUser from './pages/LoginUser';
import Profile from './pages/Profile';
import AdminPurchases from './pages/AdminPurchases';
import AdminUsers from './pages/AdminUsers';
import AdminPdfs from './pages/AdminPdfs';
import ForgotPassword from './pages/ForgotPassword';
import UpdatePassword from './pages/UpdatePassword';
import AdminRoute from './components/AdminRoute';
import Unauthorized from './pages/Unauthorized';

// ProtectedRoute component
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, authChecked } = useAuth();
  const [showedToast, setShowedToast] = useState(false);

  useEffect(() => {
    if (authChecked && !isLoggedIn && !showedToast) {
      toast.error("Unauthorized access. Please log in first.");
      setShowedToast(true);
    }
  }, [authChecked, isLoggedIn, showedToast]);

  if (!authChecked) return <Loader />;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  // Simulate a data fetch with a timeout
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 seconds delay
  }, []);

  if (isLoading) {
    return <Loader />; // Show Loader while data is loading
  }

  return (
    <HelmetProvider>
      <Router>
        <SettingsProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </SettingsProvider>
      </Router>
    </HelmetProvider>
  );
}

function AppRoutes() {
  const { user, authChecked } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();
  const location = useLocation();

  if (!authChecked || settingsLoading) {
    return <Loader />;
  }

  // Maintenance Mode Logic
  // Show maintenance page if:
  // 1. Maintenance mode is ON
  // 2. User is NOT an admin
  // 3. Current path is NOT /login (to allow admins to log in)
  const isAuthPath = location.pathname === '/login' || location.pathname === '/SignUp' || location.pathname.startsWith('/update-password');
  
  if (settings?.maintenanceMode && user?.role !== 'admin' && !isAuthPath) {
    return <Maintenance />;
  }

  return (
    <>
      <ScrollToTop />
      <ToastContainer
        position="bottom-right"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="colored"
        toastClassName="custom-toast rounded-xl shadow-2xl font-semibold text-base"
        bodyClassName="flex items-center"
        progressClassName="custom-toast-progress"
      />
      <Contactlogo />
      <div className="App min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 backdrop-blur transition-all duration-2000 ">
        <Header />
        <PageTransition>
          <Routes>
            <Route path="/" element={<Body />} />
            <Route path="/login" element={
              <GuestRoute>
                <LoginUser />
              </GuestRoute>
            } />
            <Route path="/Search" element={<Search />} />
            <Route path="/branches" element={
              <AdminRoute>
                <Branches />
              </AdminRoute>
            } />
            <Route path="/subjects/:branchId" element={
              <AdminRoute>
                <Branches />
              </AdminRoute>
            } />
            <Route path="/units/:subjectId" element={
              <AdminRoute>
                <Units />
              </AdminRoute>
            } />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/videos" element={
              <AdminRoute>
                <GetVideos />
              </AdminRoute>
            } />
            <Route path="/addvideo" element={
              <AdminRoute>
                <AddVideo />
              </AdminRoute>
            } />
            <Route path="/editvideo/:id" element={
              <AdminRoute>
                <UpdateVideo />
              </AdminRoute>
            } />
            <Route path="/SignUp" element={
              <GuestRoute>
                <SignUp />
              </GuestRoute>
            } />
            <Route path="/forgot-password" element={
              <GuestRoute>
                <ForgotPassword />
              </GuestRoute>
            } />
            <Route path="/update-password/:token" element={<UpdatePassword />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/admin/purchases" element={
              <AdminRoute>
                <AdminPurchases />
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            } />
            <Route path="/admin/pdfs" element={
              <AdminRoute>
                <AdminPdfs />
              </AdminRoute>
            } />
            <Route path="/unauthorized" element={<Unauthorized />} />
            {/* Catch-all: redirect unknown routes to unauthorized/not-found page */}
            <Route path="*" element={<Unauthorized />} />
          </Routes>
        </PageTransition>
        <Footer />
      </div>
    </>
  );
}

export default App;
